import React, { useState, useEffect } from 'react';
import { 
  FaBell, 
  FaPaperPlane, 
  FaHistory, 
  FaUsers, 
  FaEnvelope,
  FaSpinner,
  FaDownload,
  FaFilter,
  FaRedo,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { notificationService } from '../../api/notificationService';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    page: 1,
    limit: 20
  });
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    failed: 0,
    pending: 0
  });

  // Form states
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal'
  });

  const [bulkForm, setBulkForm] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal',
    scheduleTime: ''
  });

  useEffect(() => {
  
    fetchNotifications();
    fetchStats();
   
  }, [filters]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      console.log('Fetching notifications with filters:', filters);
      
      const response = await notificationService.getNotificationHistory(filters);
      console.log('Notifications API Response:', response);
      
      if (response.success) {
        console.log('Notifications data:', response.data);
        setNotifications(response.data.notifications || []);
      } else {
        console.error('API returned error:', response.message);
        toast.error(response.message || 'Failed to load notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('Fetching notification stats...');
      await notificationService.getNotificationHistory({});
      const response = await notificationService.getNotificationStats();
      console.log('Stats API Response:', response);
      
      if (response.success) {
        console.log('Stats data:', response.data);
        // Map backend stats to frontend stats
        const backendStats = response.data;
        setStats({
          total: backendStats.total || 0,
          sent: backendStats.completed || 0, // Backend uses 'completed' instead of 'sent'
          delivered: backendStats.successful || 0, // Backend uses 'successful' instead of 'delivered'
          failed: backendStats.failed || 0,
          pending: backendStats.scheduled || 0 // Backend uses 'scheduled' instead of 'pending'
        });
      } else {
        console.error('Stats API returned error:', response.message);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleSendNotification = async () => {
    try {
      if (!notificationForm.title || !notificationForm.message) {
        toast.error('Please fill in all required fields');
        return;
      }

      setLoading(true);
      
      const response = await notificationService.sendNotification(notificationForm);
      
      if (response.success) {
        toast.success('Notification sent successfully');
        setShowSendModal(false);
        setNotificationForm({
          title: '',
          message: '',
          type: 'general',
          priority: 'normal'
        });
        fetchNotifications();
        fetchStats();
      } else {
        toast.error(response.message || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkNotification = async () => {
    try {
      if (!bulkForm.title || !bulkForm.message) {
        toast.error('Please fill in all required fields');
        return;
      }

      setLoading(true);
      
      const response = await notificationService.sendBulkNotifications(bulkForm);
      
      if (response.success) {
        toast.success('Bulk notifications sent successfully');
        setShowBulkModal(false);
        setBulkForm({
          title: '',
          message: '',
          type: 'general',
          priority: 'normal',
          scheduleTime: ''
        });
        fetchNotifications();
        fetchStats();
      } else {
        toast.error(response.message || 'Failed to send bulk notifications');
      }
    } catch (error) {
      console.error('Error sending bulk notifications:', error);
      toast.error('Failed to send bulk notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      page: 1,
      limit: 20
    });
  };

  const exportNotifications = () => {
    if (notifications.length === 0) {
      toast.warning('No notifications to export');
      return;
    }

    const csvContent = [
      ['ID', 'Title', 'Message', 'Type', 'Target Users', 'Status', 'Sent At', 'Delivered', 'Failed'],
      ...notifications.map(notif => [
        notif.id,
        notif.title,
        notif.message,
        notif.type,
        notif.targetUsers,
        notif.status,
        formatDate(notif.sentAt),
        notif.deliveredCount,
        notif.failedCount
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notifications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Notifications exported successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      'sent': 'bg-green-100 text-green-800',
      'delivered': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800', // Backend uses 'completed'
      'scheduled': 'bg-yellow-100 text-yellow-800' // Backend uses 'scheduled'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'general': FaBell,
      'system': FaExclamationTriangle,
      'feature': FaInfoCircle,
      'job': FaCheckCircle,
      'maintenance': FaClock
    };
    return icons[type] || FaBell;
  };

  const getTypeColor = (type) => {
    const colors = {
      'general': 'bg-blue-100 text-blue-800',
      'system': 'bg-red-100 text-red-800',
      'feature': 'bg-green-100 text-green-800',
      'job': 'bg-purple-100 text-purple-800',
      'maintenance': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    let dateObj;
    if (date._seconds) {
      // Firestore timestamp format with _seconds and _nanoseconds
      dateObj = new Date(date._seconds * 1000);
    } else if (date.toDate) {
      // Firestore timestamp object
      dateObj = date.toDate();
    } else {
      // ISO string or Date object
      dateObj = new Date(date);
    }
    
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin h-12 w-12 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Notification Management</h1>
            <p className="text-gray-600">Send and manage system notifications</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowSendModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FaPaperPlane className="mr-2" />
              Send Notification
            </button>
            {/* <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <FaUsers className="mr-2" />
              Bulk Notifications
            </button> */}
            <button
              onClick={fetchNotifications}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
            >
              <FaRedo className="mr-2" />
              Refresh
            </button>
            {/* <button
              onClick={exportNotifications}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
            >
              <FaDownload className="mr-2" />
              Export
            </button> */}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaBell className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FaPaperPlane className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.sent}</p>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaCheckCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-gray-900">{stats.delivered}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <FaExclamationTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.failed}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FaClock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <div className="flex space-x-4">
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="general">General</option>
              <option value="system">System</option>
              <option value="feature">Feature</option>
              <option value="job">Job</option>
              <option value="maintenance">Maintenance</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>

            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notifications.length > 0 ? (
                notifications.map((notification) => {
                  const TypeIcon = getTypeIcon(notification.type);
                  return (
                    <tr key={notification.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <TypeIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate">
                              {notification.message}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(notification.type)}`}>
                          {notification.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {notification.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                          {notification.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(notification.sentAt)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <FaBell className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No notifications found</p>
                      <p className="text-sm">Try adjusting your filters or send your first notification</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Send Notification Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Notification</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notification title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notification message"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={notificationForm.type}
                    onChange={(e) => setNotificationForm({...notificationForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="general">General</option>
                    <option value="system">System</option>
                    <option value="feature">Feature</option>
                    <option value="job">Job</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={notificationForm.priority}
                    onChange={(e) => setNotificationForm({...notificationForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? <FaSpinner className="animate-spin h-4 w-4" /> : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Notifications Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Bulk Notifications</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={bulkForm.title}
                    onChange={(e) => setBulkForm({...bulkForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notification title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={bulkForm.message}
                    onChange={(e) => setBulkForm({...bulkForm, message: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notification message"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={bulkForm.type}
                    onChange={(e) => setBulkForm({...bulkForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="general">General</option>
                    <option value="system">System</option>
                    <option value="feature">Feature</option>
                    <option value="job">Job</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Time (Optional)</label>
                  <input
                    type="datetime-local"
                    value={bulkForm.scheduleTime}
                    onChange={(e) => setBulkForm({...bulkForm, scheduleTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={bulkForm.priority}
                    onChange={(e) => setBulkForm({...bulkForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkNotification}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? <FaSpinner className="animate-spin h-4 w-4" /> : 'Send Bulk'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-4 rounded-lg text-sm opacity-75 max-w-xs">
          <div className="font-bold mb-2">Debug Info</div>
          <div>Loading: {loading ? 'Yes' : 'No'}</div>
          <div>Notifications: {notifications.length}</div>
          <div>Stats Total: {stats.total}</div>
          <div>Stats Completed: {stats.sent}</div>
          <div>Stats Successful: {stats.delivered}</div>
          <div>Stats Failed: {stats.failed}</div>
          <div>Stats Scheduled: {stats.pending}</div>
        </div>
      )} */}
    </div>
  );
};

export default AdminNotifications; 